import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component';
import { ArticleTemplatePickerComponent } from '../_shared/article-template-picker';
import { ExpectedDeliveryService } from '../../_services/expected-delivery.service';
import {
  ProjectService, CustomerService, ArticleTemplateService, DeliveryNoteService, WarehouseService, SessionService,
  TicketService, UserService
} from '../../_services';
import { CountryService } from '../../_services/country.service';
import { APProject } from '../../_models/project.interface';
import { APCustomer } from '../../_models/customer.interface';
import { Warehouse } from '../../_models';
import { ArticleTemplate } from '../../_models';
import { HANDLING_INSTRUCTIONS } from '../../_models/expected-delivery.interface';
import { DatePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { switchMap, of } from 'rxjs';

@Component({
  selector: 'hawk-expected-deliveries-create',
  standalone: true,
  imports: [
    RecordNavigationComponent,
    ReactiveFormsModule,
    FormsModule,
    RouterLink,
    MatDatepickerModule,
    MatNativeDateModule,
    DatePipe,
    NgSelectModule,
    ArticleTemplatePickerComponent,
  ],
  templateUrl: './expected-deliveries-create.component.html',
  host: {
    class: 'app-page'
  }
})
export class ExpectedDeliveriesCreateComponent implements OnInit {
  deliveryForm!: FormGroup;
  projects: APProject[] = [];
  customers: APCustomer[] = [];
  articleTemplates: ArticleTemplate[] = [];
  filteredTemplates: ArticleTemplate[] = [];
  templateSearchText = '';
  isSubmitting = false;
  isLoadingTemplates = false;
  errorMessage = '';

  // Lieferschein-Erstellung
  countries: { id: string; name: string }[] = [];
  createDeliveryNote = true;
  // Bentomax Hauptlager ID
  readonly BENTOMAX_WAREHOUSE_ID = 40;
  mainWarehouse: Warehouse | null = null;

  // Interne Lieferung (Umbuchung zwischen Lagern)
  isInternalDelivery = false;
  sourceWarehouseId: number | null = null;
  sourceWarehouse: Warehouse | null = null;
  warehouses: Warehouse[] = [];
  sourceWarehouseStock: Record<number, number> = {}; // template_id -> Anzahl
  isLoadingStock = false;

  // Wizard State
  currentStep = 1;
  totalSteps = 2;

  // Verfügbare Versanddienstleister
  carriers = ['DHL', 'Hermes', 'UPS', 'DPD', 'GLS', 'FedEx', 'Andere'];

  // Handlungsanweisungen
  handlingInstructionOptions = [
    { value: HANDLING_INSTRUCTIONS.STOCK_IN, label: 'Einlagerung ins Hauptlager' },
    { value: HANDLING_INSTRUCTIONS.FORWARD, label: 'Weiterversand (an Techniker/Kunden)' },
    { value: 'OTHER', label: 'Andere (Freitext)' }
  ];
  showCustomHandlingInstruction = false;
  customHandlingInstruction = '';

  // Template Selection Modal
  showTemplateModal = false;
  templateModalItemIndex = -1;
  templateSearchQuery = '';
  filterByProject = false;
  filterByCustomer = false;
  templateDisplayLimit = 30;
  readonly TEMPLATE_PAGE_SIZE = 30;

  // Admin User Selection Modal
  showAdminUserModal = false;
  adminUsers: { id: number; name: string; email: string; selected: boolean }[] = [];
  adminUsersLoaded = false;

  // Ticket-Zuordnung
  ticketNumber = '';
  ticketCheckStatus: 'idle' | 'checking' | 'found' | 'not_found' = 'idle';
  foundTicketId: number | null = null;
  foundTicketOrderNr: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private expectedDeliveryService: ExpectedDeliveryService,
    private projectService: ProjectService,
    private customerService: CustomerService,
    private articleTemplateService: ArticleTemplateService,
    private deliveryNoteService: DeliveryNoteService,
    private countryService: CountryService,
    private warehouseService: WarehouseService,
    private sessionService: SessionService,
    private ticketService: TicketService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadData();
  }

  private initForm(): void {
    // E-Mail des aktuellen Benutzers als Default für interne Benachrichtigung
    const currentUser = this.sessionService.getCurrentUser();
    const defaultInternalEmail = currentUser?.email1 || '';

    this.deliveryForm = this.fb.group({
      order_nr: [''],
      supplier_name: [''],
      tracking_numbers: this.fb.array([]),
      ticket_id: [null],
      project_id: [null],
      customer_id: [null],
      packaging_type: [''],
      handling_instruction: ['', Validators.required],
      notes: [''],
      internal_notification_emails: [defaultInternalEmail],
      external_notification_emails: [''],
      order_date: [new Date()],
      expected_from: [null],
      expected_until: [null],
      expected_delivery_items_attributes: this.fb.array([]),
      // Absender-Adresse für internen Lieferschein
      sender_address_company: [''],
      sender_address_firstname: [''],
      sender_address_lastname: [''],
      sender_address_street: [''],
      sender_address_street_no: [''],
      sender_address_zip: [''],
      sender_address_city: [''],
      sender_address_country: ['deu']
    });
  }

  private loadData(): void {
    // admin_page_hash laden (inkl. customer_id für automatische Kundenzuordnung), auf aktive filtern
    this.projectService.getAPProjects().subscribe({
      next: (data) => this.projects = data.filter(p => p.active),
      error: (err) => console.error('Fehler beim Laden der Projekte:', err)
    });

    this.customerService.loadAPCustomers().subscribe({
      next: (data) => this.customers = data.sort((a, b) => a.name.localeCompare(b.name)),
      error: (err) => console.error('Fehler beim Laden der Kunden:', err)
    });

    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
        this.filteredTemplates = [...data];
      },
      error: (err) => console.error('Fehler beim Laden der Artikelvorlagen:', err)
    });

    this.countryService.list().subscribe({
      next: (data) => this.countries = data,
      error: (err) => console.error('Fehler beim Laden der Länder:', err)
    });

    // Hauptlager laden für Lieferschein-Empfänger
    this.warehouseService.loadWarehouse(this.BENTOMAX_WAREHOUSE_ID).subscribe({
      next: (data) => this.mainWarehouse = data,
      error: (err) => console.error('Fehler beim Laden des Hauptlagers:', err)
    });

    // Alle Lager laden für interne Lieferung (Hauptlager ausschließen)
    this.warehouseService.loadWarehouses().subscribe({
      next: (data) => this.warehouses = data.filter(w => w.id !== this.BENTOMAX_WAREHOUSE_ID),
      error: (err) => console.error('Fehler beim Laden der Lager:', err)
    });
  }

  get items(): FormArray {
    return this.deliveryForm.get('expected_delivery_items_attributes') as FormArray;
  }

  get trackingNumbers(): FormArray {
    return this.deliveryForm.get('tracking_numbers') as FormArray;
  }

  addTrackingNumber(): void {
    const trackingGroup = this.fb.group({
      carrier: ['DHL'],
      number: ['']
    });
    this.trackingNumbers.push(trackingGroup);
  }

  removeTrackingNumber(index: number): void {
    this.trackingNumbers.removeAt(index);
  }

  /**
   * Formatiert die Tracking-Nummern zu einem Semicolon-getrennten String
   * Format: DHL:12345;Hermes:67890
   */
  private formatTrackingNumbers(): string {
    const trackingEntries = this.trackingNumbers.value as { carrier: string; number: string }[];
    return trackingEntries
      .filter(entry => entry.number?.trim())
      .map(entry => `${entry.carrier}:${entry.number.trim()}`)
      .join(';');
  }

  // Wizard Navigation
  get isStep1Valid(): boolean {
    // Handlungsanweisung ist Pflichtfeld
    const handlingInstruction = this.deliveryForm.get('handling_instruction')?.value;
    return !!handlingInstruction && handlingInstruction.trim().length > 0;
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.isStep1Valid) {
      this.currentStep = 2;
      this.errorMessage = '';
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.errorMessage = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Helper für Zusammenfassung
  getProjectName(): string {
    const projectId = this.deliveryForm.get('project_id')?.value;
    if (!projectId) return '-';
    const project = this.projects.find(p => p.id === projectId);
    return project?.name || '-';
  }

  getCustomerName(): string {
    const customerId = this.deliveryForm.get('customer_id')?.value;
    if (!customerId) return '-';
    const customer = this.customers.find(c => c.id === customerId);
    return customer?.name || '-';
  }

  getPackagingLabel(): string {
    const type = this.deliveryForm.get('packaging_type')?.value;
    if (!type) return '-';
    return type === 'PALLET' ? 'Palette' : 'Paket';
  }

  getHandlingInstructionLabel(): string {
    const value = this.deliveryForm.get('handling_instruction')?.value;
    if (!value) return '-';
    const option = this.handlingInstructionOptions.find(o => o.value === value);
    return option ? option.label : value; // Freitext wird direkt angezeigt
  }

  onHandlingInstructionChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    if (value === 'OTHER') {
      this.showCustomHandlingInstruction = true;
      this.deliveryForm.get('handling_instruction')?.setValue('');
    } else {
      this.showCustomHandlingInstruction = false;
      this.customHandlingInstruction = '';
    }
  }

  onCustomHandlingInstructionChange(): void {
    this.deliveryForm.get('handling_instruction')?.setValue(this.customHandlingInstruction);
  }

  getTrackingNumbersSummary(): string {
    const trackingEntries = this.trackingNumbers.value as { carrier: string; number: string }[];
    const validEntries = trackingEntries.filter(e => e.number?.trim());
    if (validEntries.length === 0) return '-';
    return validEntries.map(e => `${e.carrier}: ${e.number}`).join(', ');
  }

  addItem(): void {
    // Owner basierend auf Kundenauswahl setzen
    const hasCustomer = !!this.deliveryForm.get('customer_id')?.value;
    const defaultOwner = hasCustomer ? 'CUSTOMER' : 'BENTOMAX';

    const itemGroup = this.fb.group({
      article_template_id: [null, Validators.required],
      quantity_ordered: [1, [Validators.required, Validators.min(1)]],
      owner: [defaultOwner, Validators.required]
    });
    this.items.push(itemGroup);
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
  }

  filterTemplates(event: Event): void {
    const searchText = (event.target as HTMLInputElement).value.toLowerCase();
    this.templateSearchText = searchText;
    this.filteredTemplates = this.articleTemplates.filter(t =>
      t.description?.toLowerCase().includes(searchText) ||
      t.part_nr?.toLowerCase().includes(searchText) ||
      t.vendor?.toLowerCase().includes(searchText)
    );
  }

  // Template Modal Methods
  openTemplateModal(itemIndex: number): void {
    this.templateModalItemIndex = itemIndex;
    this.templateSearchQuery = '';
    this.filterByProject = false;
    // Kundenfilter automatisch aktivieren wenn ein Kunde ausgewählt ist
    this.filterByCustomer = !!this.deliveryForm.get('customer_id')?.value;
    this.applyTemplateFilters();
    this.showTemplateModal = true;
  }

  closeTemplateModal(): void {
    this.showTemplateModal = false;
    this.templateModalItemIndex = -1;
  }

  selectTemplate(template: ArticleTemplate): void {
    if (this.templateModalItemIndex >= 0 && this.templateModalItemIndex < this.items.length) {
      const itemControl = this.items.at(this.templateModalItemIndex);
      itemControl.get('article_template_id')?.setValue(template.id);
    }
    this.closeTemplateModal();
  }

  /**
   * Wird aufgerufen wenn der Picker die Templates neu lädt
   */
  onPickerTemplatesReloaded(templates: ArticleTemplate[]): void {
    this.articleTemplates = templates;
    this.filteredTemplates = templates;
  }

  /**
   * Gibt das Projekt für den Picker zurück
   */
  getPickerProject(): { id: number; name: string } | null {
    const projectId = this.deliveryForm.get('project_id')?.value;
    if (!projectId) return null;
    const project = this.projects.find(p => p.id === projectId);
    return project ? { id: project.id, name: project.name } : null;
  }

  /**
   * Gibt den Kunden für den Picker zurück
   */
  getPickerCustomer(): { id: number; name: string } | null {
    const customerId = this.deliveryForm.get('customer_id')?.value;
    if (!customerId) return null;
    const customer = this.customers.find(c => c.id === customerId);
    return customer ? { id: customer.id, name: customer.name } : null;
  }

  applyTemplateFilters(): void {
    let filtered = [...this.articleTemplates];

    // Text search
    if (this.templateSearchQuery.trim()) {
      const query = this.templateSearchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.description?.toLowerCase().includes(query) ||
        t.part_nr?.toLowerCase().includes(query) ||
        t.vendor?.toLowerCase().includes(query)
      );
    }

    // Filter by project
    if (this.filterByProject) {
      const projectId = this.deliveryForm.get('project_id')?.value;
      if (projectId) {
        filtered = filtered.filter(t => {
          if (!t.comment2) return false;
          const projectIds = t.comment2.split(',').map(id => id.trim());
          return projectIds.includes(String(projectId));
        });
      }
    }

    // Filter by customer
    if (this.filterByCustomer) {
      const customerId = this.deliveryForm.get('customer_id')?.value;
      if (customerId) {
        filtered = filtered.filter(t => {
          if (!t.comment3) return false;
          return t.comment3.trim() === String(customerId);
        });
      }
    }

    this.filteredTemplates = filtered;
    // Reset display limit when filters change
    this.templateDisplayLimit = this.TEMPLATE_PAGE_SIZE;
  }

  get visibleTemplates(): ArticleTemplate[] {
    return this.filteredTemplates.slice(0, this.templateDisplayLimit);
  }

  get hasMoreTemplates(): boolean {
    return this.filteredTemplates.length > this.templateDisplayLimit;
  }

  get remainingTemplatesCount(): number {
    return this.filteredTemplates.length - this.templateDisplayLimit;
  }

  showMoreTemplates(): void {
    this.templateDisplayLimit += this.TEMPLATE_PAGE_SIZE;
  }

  /**
   * Lädt die Artikelvorlagen neu (z.B. nach Erstellung einer neuen Vorlage)
   */
  reloadArticleTemplates(): void {
    this.isLoadingTemplates = true;
    this.articleTemplateService.all().subscribe({
      next: (data) => {
        this.articleTemplates = data;
        this.applyTemplateFilters();
        this.isLoadingTemplates = false;
      },
      error: (err) => {
        console.error('Fehler beim Laden der Artikelvorlagen:', err);
        this.isLoadingTemplates = false;
      }
    });
  }

  onTemplateSearchChange(event: Event): void {
    this.templateSearchQuery = (event.target as HTMLInputElement).value;
    this.applyTemplateFilters();
  }

  toggleProjectFilter(): void {
    this.filterByProject = !this.filterByProject;
    this.applyTemplateFilters();
  }

  toggleCustomerFilter(): void {
    this.filterByCustomer = !this.filterByCustomer;
    this.applyTemplateFilters();
  }

  getSelectedProjectName(): string {
    const projectId = this.deliveryForm.get('project_id')?.value;
    if (!projectId) return '';
    const project = this.projects.find(p => p.id === projectId);
    return project?.name || '';
  }

  getSelectedCustomerName(): string {
    const customerId = this.deliveryForm.get('customer_id')?.value;
    if (!customerId) return '';
    const customer = this.customers.find(c => c.id === customerId);
    return customer?.name || '';
  }

  getTemplateName(templateId: number): string {
    const template = this.articleTemplates.find(t => t.id === templateId);
    return template ? `${template.description} (${template.part_nr || 'N/A'})` : 'Nicht ausgewählt';
  }

  onSubmit(): void {
    if (this.deliveryForm.invalid) {
      this.errorMessage = 'Bitte füllen Sie alle Pflichtfelder aus.';
      return;
    }

    if (this.items.length === 0) {
      this.errorMessage = 'Bitte fügen Sie mindestens eine Position hinzu.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const formData = { ...this.deliveryForm.value };

    // Tracking-Nummern formatieren und als external_delivery_note_number speichern
    formData.external_delivery_note_number = this.formatTrackingNumbers();
    delete formData.tracking_numbers; // Nicht an Backend senden

    // Absender-Adressfelder entfernen (werden nur für Lieferschein gebraucht)
    const senderAddress = {
      sender_address_company: formData.sender_address_company,
      sender_address_firstname: formData.sender_address_firstname,
      sender_address_lastname: formData.sender_address_lastname,
      sender_address_street: formData.sender_address_street,
      sender_address_street_no: formData.sender_address_street_no,
      sender_address_zip: formData.sender_address_zip,
      sender_address_city: formData.sender_address_city,
      sender_address_country: formData.sender_address_country
    };
    delete formData.sender_address_company;
    delete formData.sender_address_firstname;
    delete formData.sender_address_lastname;
    delete formData.sender_address_street;
    delete formData.sender_address_street_no;
    delete formData.sender_address_zip;
    delete formData.sender_address_city;
    delete formData.sender_address_country;

    // Interne Lieferung: source_warehouse_id setzen
    if (this.isInternalDelivery && this.sourceWarehouseId) {
      formData.source_warehouse_id = this.sourceWarehouseId;
    }

    // Datum-Formatierung
    if (formData.order_date) {
      formData.order_date = this.formatDate(formData.order_date);
    }
    if (formData.expected_from) {
      formData.expected_from = this.formatDate(formData.expected_from);
    }
    if (formData.expected_until) {
      formData.expected_until = this.formatDate(formData.expected_until);
    }

    // Wenn Lieferschein erstellt werden soll, erst DeliveryNote erstellen
    const deliveryNote$ = this.createDeliveryNote && this.mainWarehouse
      ? this.deliveryNoteService.create({
          delivery_note: {
            sender_type: 'FREE',
            ...senderAddress,
            recipient_type: 'WAREHOUSE',
            recipient_warehouse_id: this.BENTOMAX_WAREHOUSE_ID,
            // Empfänger-Adresse aus dem Hauptlager
            recipient_address_company: this.mainWarehouse.address_company,
            recipient_address_firstname: this.mainWarehouse.address_firstname,
            recipient_address_lastname: this.mainWarehouse.address_lastname,
            recipient_address_street: this.mainWarehouse.address_street,
            recipient_address_street_no: (this.mainWarehouse as any).address_street_no || '',
            recipient_address_zip: this.mainWarehouse.address_zip,
            recipient_address_city: this.mainWarehouse.address_city,
            recipient_address_country: this.mainWarehouse.address_country,
            info: `Wareneingang: ${formData.order_nr}`
          }
        })
      : of(null);

    deliveryNote$.pipe(
      switchMap((deliveryNoteResponse: any) => {
        // Wenn Lieferschein erstellt wurde, ID speichern
        if (deliveryNoteResponse?.id) {
          formData.delivery_note_id = deliveryNoteResponse.id;
        }
        return this.expectedDeliveryService.create(formData);
      })
    ).subscribe({
      next: (response) => {
        this.router.navigate(['/lager/erwartete-lieferungen', response.id]);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err.error?.errors?.join(', ') || err.error?.message || 'Fehler beim Erstellen der Lieferung.';
        console.error('Fehler:', err);
      }
    });
  }

  private formatDate(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    // Lokales Datum verwenden (nicht UTC), um Timezone-Probleme zu vermeiden
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Admin User Selection Modal Methods

  /**
   * Öffnet das Admin-User-Auswahl-Modal
   */
  openAdminUserModal(): void {
    if (!this.adminUsersLoaded) {
      this.loadAdminUsers();
    } else {
      this.updateAdminUserSelection();
    }
    this.showAdminUserModal = true;
  }

  /**
   * Schließt das Admin-User-Auswahl-Modal
   */
  closeAdminUserModal(): void {
    this.showAdminUserModal = false;
  }

  /**
   * Lädt die Admin-User aus dem Backend
   */
  private loadAdminUsers(): void {
    this.userService.getAdminUsers().subscribe({
      next: (users) => {
        this.adminUsers = users.map(u => ({
          ...u,
          selected: this.isEmailAlreadySelected(u.email)
        }));
        this.adminUsersLoaded = true;
      },
      error: (err) => console.error('Fehler beim Laden der Admin-User:', err)
    });
  }

  /**
   * Aktualisiert die Auswahl basierend auf den aktuellen E-Mails im Formular
   */
  private updateAdminUserSelection(): void {
    this.adminUsers = this.adminUsers.map(u => ({
      ...u,
      selected: this.isEmailAlreadySelected(u.email)
    }));
  }

  /**
   * Prüft ob eine E-Mail bereits in der internen Benachrichtigungsliste ist
   */
  private isEmailAlreadySelected(email: string): boolean {
    const currentEmails = this.deliveryForm.get('internal_notification_emails')?.value || '';
    const emailList = currentEmails.split(';').map((e: string) => e.trim().toLowerCase());
    return emailList.includes(email.toLowerCase());
  }

  /**
   * Toggle für Admin-User-Auswahl
   */
  toggleAdminUser(user: { id: number; name: string; email: string; selected: boolean }): void {
    user.selected = !user.selected;
  }

  /**
   * Wendet die Auswahl an und fügt die E-Mails zum Formular hinzu
   */
  applyAdminUserSelection(): void {
    const currentEmails = this.deliveryForm.get('internal_notification_emails')?.value || '';
    const emailSet = new Set(
      currentEmails.split(';').map((e: string) => e.trim().toLowerCase()).filter((e: string) => e)
    );

    // Ausgewählte E-Mails hinzufügen, nicht-ausgewählte entfernen
    this.adminUsers.forEach(user => {
      const emailLower = user.email.toLowerCase();
      if (user.selected) {
        emailSet.add(user.email); // Original-Format behalten
      } else {
        emailSet.delete(emailLower);
      }
    });

    // Formular aktualisieren
    const newValue = Array.from(emailSet).join('; ');
    this.deliveryForm.get('internal_notification_emails')?.setValue(newValue);

    this.closeAdminUserModal();
  }

  // Ticket-Zuordnung Methods

  /**
   * Prüft ob ein Ticket mit der eingegebenen Order-Nr existiert
   */
  checkTicket(): void {
    const ticketNr = this.ticketNumber.trim();

    if (!ticketNr) {
      this.ticketCheckStatus = 'idle';
      this.foundTicketId = null;
      this.foundTicketOrderNr = null;
      this.deliveryForm.get('ticket_id')?.setValue(null);
      return;
    }

    this.ticketCheckStatus = 'checking';

    // Suche nach order_nr statt id
    this.ticketService.findTicketGroupByOrderNr(ticketNr).subscribe({
      next: (response) => {
        this.ticketCheckStatus = 'found';
        this.foundTicketId = response.ticket.id;
        this.foundTicketOrderNr = response.ticket.order_nr;
        this.deliveryForm.get('ticket_id')?.setValue(response.ticket.id);

        // Projekt automatisch auswählen wenn vorhanden
        if (response.ticket.project_id) {
          const projectId = Number(response.ticket.project_id);
          const project = this.projects.find(p => p.id === projectId);
          if (project) {
            this.deliveryForm.patchValue({ project_id: projectId });
            // Kunde und Absenderadresse vom Projekt übernehmen
            this.onProjectChange();
          }
        }
      },
      error: () => {
        this.ticketCheckStatus = 'not_found';
        this.foundTicketId = null;
        this.foundTicketOrderNr = null;
        this.deliveryForm.get('ticket_id')?.setValue(null);
      }
    });
  }

  /**
   * Wird aufgerufen wenn sich die Ticket-Nummer ändert
   */
  onTicketNumberChange(): void {
    // Reset status wenn Eingabe geändert wird
    if (this.ticketCheckStatus !== 'idle') {
      this.ticketCheckStatus = 'idle';
      this.foundTicketId = null;
      this.deliveryForm.get('ticket_id')?.setValue(null);
    }
  }

  /**
   * Löscht die Ticket-Zuordnung
   */
  clearTicket(): void {
    this.ticketNumber = '';
    this.ticketCheckStatus = 'idle';
    this.foundTicketId = null;
    this.foundTicketOrderNr = null;
    this.deliveryForm.get('ticket_id')?.setValue(null);
  }

  /**
   * Wird aufgerufen wenn das Projekt manuell geändert wird
   * Setzt automatisch den Kunden basierend auf der Projektzuordnung
   */
  onProjectChange(): void {
    const projectId = this.deliveryForm.get('project_id')?.value;
    if (projectId) {
      const project = this.projects.find(p => p.id === projectId);
      if (project?.customer?.id) {
        this.deliveryForm.patchValue({ customer_id: project.customer.id });
        this.updateItemOwners();
        this.updateSenderAddressFromCustomer();
      }
    }
  }

  /**
   * Wird aufgerufen wenn der Kunde manuell geändert wird
   * Übernimmt automatisch die Kundenadresse für den internen Lieferschein
   */
  onCustomerChange(): void {
    this.updateItemOwners();
    this.updateSenderAddressFromCustomer();
  }

  /**
   * Übernimmt die Adresse des ausgewählten Kunden als Absender-Adresse
   */
  private updateSenderAddressFromCustomer(): void {
    const customerId = this.deliveryForm.get('customer_id')?.value;
    if (customerId) {
      const customer = this.customers.find(c => c.id === customerId);
      if (customer) {
        this.deliveryForm.patchValue({
          sender_address_company: customer.company || customer.name,
          sender_address_street: customer.street || '',
          sender_address_zip: customer.zip || '',
          sender_address_city: customer.city || '',
          sender_address_country: customer.country || 'deu',
          // Vorname/Nachname und Hausnummer werden bei Kunden nicht separat gespeichert
          sender_address_firstname: '',
          sender_address_lastname: '',
          sender_address_street_no: ''
        });
      }
    } else {
      // Kein Kunde ausgewählt - Adressfelder leeren
      this.deliveryForm.patchValue({
        sender_address_company: '',
        sender_address_firstname: '',
        sender_address_lastname: '',
        sender_address_street: '',
        sender_address_street_no: '',
        sender_address_zip: '',
        sender_address_city: '',
        sender_address_country: 'deu'
      });
    }
  }

  /**
   * Gibt den ausgewählten Kunden zurück (für Template-Anzeige)
   */
  getSelectedCustomer(): APCustomer | null {
    const customerId = this.deliveryForm.get('customer_id')?.value;
    if (!customerId) return null;
    return this.customers.find(c => c.id === customerId) || null;
  }

  // Interne Lieferung Methods

  /**
   * Wird aufgerufen wenn der Toggle "Interne Lieferung" geändert wird
   */
  onInternalDeliveryToggle(): void {
    if (!this.isInternalDelivery) {
      // Deaktiviert: Quelllager und Stock zurücksetzen
      this.sourceWarehouseId = null;
      this.sourceWarehouse = null;
      this.sourceWarehouseStock = {};
      // Absender-Adresse ggf. vom Kunden wiederherstellen
      this.updateSenderAddressFromCustomer();
    }
  }

  /**
   * Wird aufgerufen wenn ein Quelllager ausgewählt wird
   */
  onSourceWarehouseChange(warehouseId: number | null): void {
    this.sourceWarehouseId = warehouseId;
    this.sourceWarehouseStock = {};

    if (!warehouseId) {
      this.sourceWarehouse = null;
      this.updateSenderAddressFromCustomer();
      return;
    }

    // Vorläufig aus der Liste nehmen (hat nur address_city)
    this.sourceWarehouse = this.warehouses.find(w => w.id === warehouseId) || null;

    // Lieferant auf Lagername setzen
    if (this.sourceWarehouse) {
      this.deliveryForm.patchValue({ supplier_name: this.sourceWarehouse.label });
    }

    // Lagerbestand + Adresse in einem Request laden
    this.isLoadingStock = true;
    this.expectedDeliveryService.getWarehouseStock(warehouseId).subscribe({
      next: (response) => {
        // Adresse aus warehouse (address_detail_hash) übernehmen
        const wh = response.warehouse;
        if (wh) {
          // sourceWarehouse mit vollständigen Adressdaten anreichern
          Object.assign(this.sourceWarehouse!, wh);

          // Absender-Adresse ins Formular übernehmen
          const streetWithNo = [wh.address_street, wh.address_street_no].filter(Boolean).join(' ');
          this.deliveryForm.patchValue({
            sender_address_company: wh.address_company || wh.label,
            sender_address_firstname: wh.address_firstname || '',
            sender_address_lastname: wh.address_lastname || '',
            sender_address_street: streetWithNo,
            sender_address_street_no: '',
            sender_address_zip: wh.address_zip || '',
            sender_address_city: wh.address_city || '',
            sender_address_country: wh.address_country || 'deu'
          });
        }

        // Stock-Keys sind Strings (JSON), in Number-Map umwandeln
        this.sourceWarehouseStock = {};
        for (const [key, value] of Object.entries(response.stock)) {
          this.sourceWarehouseStock[Number(key)] = value as number;
        }
        this.isLoadingStock = false;
      },
      error: (err) => {
        console.error('Fehler beim Laden des Lagerbestands:', err);
        this.isLoadingStock = false;
      }
    });
  }

  /**
   * Gibt die Artikelvorlagen-IDs zurück die im Quelllager vorhanden sind
   */
  getWarehouseArticleTemplateIds(): number[] | null {
    const ids = Object.keys(this.sourceWarehouseStock).map(Number);
    return ids.length > 0 ? ids : null;
  }

  /**
   * Gibt den Lagerbestand für eine Artikelvorlage im Quelllager zurück
   */
  getSourceWarehouseStockForTemplate(templateId: number | null): number {
    if (!templateId || !this.isInternalDelivery) return 0;
    return this.sourceWarehouseStock[templateId] || 0;
  }

  /**
   * Aktualisiert den Owner aller Positionen basierend auf Kundenauswahl
   */
  private updateItemOwners(): void {
    const hasCustomer = !!this.deliveryForm.get('customer_id')?.value;
    const newOwner = hasCustomer ? 'CUSTOMER' : 'BENTOMAX';

    this.items.controls.forEach(item => {
      item.get('owner')?.setValue(newOwner);
    });
  }
}
